import * as R from 'remeda';
import { IntlShape } from 'react-intl';
import { z } from 'zod';
import { HttpError } from '@rossum/api-client/errors';
import { notify } from '../features/toaster';

const isTooMayImportsError = (
  error: unknown
): error is { status: [{ detail: string; code: string }] } => {
  return (
    R.isObjectType(error) &&
    'status' in error &&
    R.isArray(error.status) &&
    error.status.length > 0 &&
    R.isObjectType(error.status[0]) &&
    'code' in error.status[0] &&
    error.status[0].code === 'too_many_imports'
  );
};
export const handleTooManyImportsError = (error: unknown, intl: IntlShape) => {
  if (
    error instanceof HttpError &&
    error.code === 400 &&
    isTooMayImportsError(error.data)
  ) {
    notify.error({
      title: intl.formatMessage({
        id: 'containers.message.tooManyReimports',
      }),
    });
    return;
  }
  throw error;
};

const flattenObj = (
  obj: object,
  savedKey: string | null = null,
  result: Record<string, string> = {}
): Record<string, string> => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    const path = savedKey ? `${savedKey}.${key}` : key;

    if (value && typeof value === 'object') {
      return flattenObj(value, path, acc);
    }

    if (typeof value === 'string') return { ...acc, [path]: value };

    return acc;
  }, result);
};

const objectSchema = z
  .object({ data: z.record(z.unknown()).default({}) })
  .default({ data: {} })
  .catch({ data: {} });

export const getExpectedErrorKey = <T extends string>({
  error,
  expectedError,
}: {
  error: unknown;
  expectedError: { key: T; value: string };
}) => {
  if (!error) return null;

  const parsedError = objectSchema.parse(error);

  const flattenedError = flattenObj(parsedError.data);

  return Object.entries(flattenedError).reduce<'unknown-error' | T>(
    (acc, [_, value]) => {
      if (value.toLowerCase().includes(expectedError.value.toLowerCase()))
        return expectedError.key;

      return acc;
    },
    'unknown-error'
  );
};
